import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardList,
  Truck,
  Boxes,
  CheckCheck,
  ArrowUpRight,
  Plus,
  ChevronRight,
} from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  Eyebrow,
  Heading,
  KPICard,
  Skeleton,
  StatusBadge,
  type StatusKey,
} from '@insolech/ui';
import { dashboardQueries, orderQueries } from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { formatProductLabel, resolvePatientDisplay } from '@/features/orders/productLabel';

/**
 * DashboardPage — podiatrist home.
 *
 * Phase 4a wiring: real Supabase queries (clinic-scoped via RLS).
 *  - KPI cards: open / in-production / awaiting-pickup / delivered-30d
 *  - Recent activity: last 10 status transitions on the clinic's orders
 *  - Quick actions: New order CTA, search by short code
 *  - Empty states for fresh clinics
 */

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

export function DashboardPage() {
  const { t } = useTranslation(['orders', 'common']);
  const { user, fullName, clinicId, clinics, profileLoading } = useAuth();
  const navigate = useNavigate();
  const greeting = greetingForHour(new Date().getHours());
  const firstName = (
    fullName?.split(' ')[0] ??
    (user?.email ?? 'there').split('@')[0] ??
    'there'
  ).split('.')[0];

  // Multi-clinic podiatrist relax (mirrors OrderTrackerPage). `clinics`
  // from useAuth() is already filtered to active memberships inside
  // resolveProfileAndRole, so length > 1 is the multi-clinic signal.
  // When the viewer is multi-clinic we send clinicId=null so the RPCs
  // count across every membership instead of bucketing them into the
  // single defaultClinicId. Strict isolation by podiatrist_id keeps
  // them from seeing colleagues' orders at any of those clinics.
  const multiClinicPodiatrist = clinics.length > 1;
  const clinicIdForDashboard = multiClinicPodiatrist ? null : clinicId;
  const podiatristIdFilter = user?.id ?? null;
  // All three queries share the same enabled gate: as soon as we know
  // the user id AND have either a resolved clinicId OR we know the
  // user is multi-clinic (in which case clinicIdForDashboard is
  // intentionally null), fire away.
  const queriesEnabled =
    !!user?.id && (multiClinicPodiatrist || !!clinicId);

  const kpis = useQuery({
    ...dashboardQueries.podiatristKpisQuery(
      supabase,
      clinicIdForDashboard,
      podiatristIdFilter,
    ),
    enabled: queriesEnabled,
  });
  // Per-status counts (shares the tracker's query so the dashboard cards
  // and the /orders status boxes can never drift). The server-side RPC
  // above (podiatrist_dashboard_kpis) historically defined OPEN as
  // "everything not delivered/cancelled/returned", which double-counts
  // in_delivery (it lit up both OPEN and AWAITING PICKUP). We recompute
  // open/in_production/awaiting_pickup from these per-status counts so
  // the four cards are mutually exclusive and the IN PRODUCTION number
  // matches the /orders tracker exactly. DELIVERED (30d) still comes
  // from the RPC because it needs the date filter the counts query does
  // not perform.
  const statusCounts = useQuery({
    ...orderQueries.ordersStatusCountsQuery(supabase, {
      clinicId: clinicIdForDashboard,
      podiatristId: podiatristIdFilter,
    }),
    enabled: queriesEnabled,
  });
  // OPEN ORDERS = draft + submitted + in_production (everything earlier
  // than delivery; excludes in_delivery, delivered, cancelled, returned,
  // on_hold). Matches Rafael's spec: "all orders whose status is NOT in
  // (delivery, delivered, cancelled, returned)" minus on_hold which is
  // also pre-delivery but parked. The ordersStatusCountsQuery already
  // excludes on_hold / in_review / cancelled / returned at the source
  // (it only counts the simplified R28 enum set), so summing its four
  // pre-delivery buckets is the right answer.
  const sc = statusCounts.data;
  const openOrdersCount =
    (sc?.pending ?? 0) +
    (sc?.submitted ?? 0) +
    (sc?.in_production ?? 0);
  const inProductionCount = sc?.in_production ?? 0;
  const awaitingPickupCount = sc?.in_delivery ?? 0;
  const deliveredCount = kpis.data?.delivered_30d ?? 0;
  const kpisLoading = kpis.isLoading || statusCounts.isLoading;
  const activity = useQuery({
    ...dashboardQueries.recentActivityQuery(
      supabase,
      clinicIdForDashboard,
      10,
      podiatristIdFilter,
    ),
    enabled: queriesEnabled,
  });
  // KPI cards double as a filter on the Active orders list below. Default
  // is 'active' so the list opens with every non-terminal order; clicking a
  // KPI card narrows the list to that status. Re-clicking the same card has
  // no semantic effect (admin pattern, no toggle-off). Mirrors AdminPage.
  const [selectedStatus, setSelectedStatus] = React.useState<orderQueries.OrderStatusFilter>('active');

  const recentOrders = useQuery({
    ...orderQueries.ordersListQuery(supabase, {
      clinicId: clinicIdForDashboard,
      status: selectedStatus,
      pageSize: 5,
      sortBy: 'created_at',
      sortDir: 'desc',
      podiatristId: podiatristIdFilter,
    }),
    enabled: queriesEnabled,
  });

  return (
    <AppShell>
      <main className="px-4 sm:px-6 py-5 max-w-[1400px] mx-auto">
        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <Eyebrow>{t('orders:title_dashboard')}</Eyebrow>
            <Heading as="h1" size="h3" className="mt-2 capitalize">
              {greeting}, {firstName}.
            </Heading>
            {/*
              Phase 4b iter round 2 — Fix D: gate the "not yet linked to a
              clinic" message on profileLoading. Without this, the dashboard
              false-positives during the AuthGate-released-but-profile-not-yet-
              resolved window (Rafael's screenshot showed all KPIs at 0 +
              the "not linked" copy on a correctly-linked demo account).
            */}
            {profileLoading ? (
              <p className="text-text-muted mt-1" data-testid="dashboard-loading-clinic">
                Loading your clinic…
              </p>
            ) : clinicId || multiClinicPodiatrist ? (
              <p className="text-text-secondary mt-1" data-testid="dashboard-clinic-orders">
                You have{' '}
                <span className="font-medium text-text-primary">
                  {sc ? openOrdersCount : '–'} open orders
                </span>{' '}
                in flight.
              </p>
            ) : (
              <p
                className="text-text-secondary mt-1"
                data-testid="dashboard-not-linked"
              >
                You&apos;re not yet linked to a clinic. Ask your admin for an invitation.
              </p>
            )}
          </div>
          <Button
            size="lg"
            startIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/orders/new')}
            disabled={profileLoading || !clinicId}
          >
            {t('orders:action_new_order')}
          </Button>
        </div>

        {/* KPI row */}
        {/* Mobile: 2-up KPI grid (4 stacked full-width cards pushed the
            orders list ~450px below the fold). >=640px unchanged. */}
        <div className="grid grid-cols-2 max-sm:gap-3 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpisLoading ? (
            Array.from({ length: 4 }).map((_, i) => <KpiSkeleton key={i} />)
          ) : (
            <>
              <KpiFilterButton
                filter="active"
                selected={selectedStatus}
                onSelect={setSelectedStatus}
                ariaLabel="Filter active orders by: open"
              >
                <KPICard
                  label={t('orders:kpi_open_orders')}
                  value={String(openOrdersCount)}
                  icon={<ClipboardList className="h-4 w-4" />}
                  accentColor="brand"
                />
              </KpiFilterButton>
              <KpiFilterButton
                filter="production"
                selected={selectedStatus}
                onSelect={setSelectedStatus}
                ariaLabel="Filter active orders by: in production"
              >
                <KPICard
                  label={t('orders:kpi_in_production')}
                  value={String(inProductionCount)}
                  icon={<Boxes className="h-4 w-4" />}
                  accentColor="info"
                />
              </KpiFilterButton>
              <KpiFilterButton
                filter="delivery"
                selected={selectedStatus}
                onSelect={setSelectedStatus}
                ariaLabel="Filter active orders by: awaiting pickup"
              >
                <KPICard
                  label={t('orders:kpi_awaiting_pickup')}
                  value={String(awaitingPickupCount)}
                  icon={<Truck className="h-4 w-4" />}
                  accentColor="warning"
                />
              </KpiFilterButton>
              <KpiFilterButton
                filter="delivered"
                selected={selectedStatus}
                onSelect={setSelectedStatus}
                ariaLabel="Filter active orders by: delivered"
              >
                <KPICard
                  label={t('orders:kpi_delivered_30d')}
                  value={String(deliveredCount)}
                  icon={<CheckCheck className="h-4 w-4" />}
                  accentColor="success"
                />
              </KpiFilterButton>
            </>
          )}
        </div>

        {/* Main split */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active orders, 2/3. Title is dynamic so it reflects the KPI
              card the user clicked above. */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{KPI_FILTER_TITLE[selectedStatus] ?? 'Active orders'}</CardTitle>
                <CardDescription>The most recent orders in your pipeline.</CardDescription>
              </div>
              <Link to="/orders" className="text-sm text-brand-700 hover:underline inline-flex items-center gap-1">
                View all <ChevronRight className="h-4 w-4 rtl-flip" />
              </Link>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              {recentOrders.isLoading ? (
                <ul className="divide-y divide-border-subtle">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="px-6 py-3">
                      <Skeleton className="h-6 w-full" />
                    </li>
                  ))}
                </ul>
              ) : (recentOrders.data?.rows.length ?? 0) === 0 ? (
                <EmptyState
                  icon={<ClipboardList className="h-6 w-6" />}
                  title={t('orders:empty_no_orders_title')}
                  description={t('orders:empty_no_orders_body')}
                />
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {recentOrders.data!.rows.map((order) => (
                    <li
                      key={order.id}
                      onClick={() => navigate(`/orders/${order.id}`)}
                      className="px-6 py-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-center hover:bg-sand-50 transition-colors cursor-pointer"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">
                          {resolvePatientDisplay(order).name ?? '-'}
                        </div>
                        <div className="text-xs text-text-muted font-mono">
                          {order.primary_line?.short_code ?? order.order_number}
                        </div>
                      </div>
                      <div className="text-sm text-text-secondary truncate">
                        {formatProductLabel(order.primary_line)}
                        <div className="text-xs text-text-muted font-mono">
                          {order.order_number}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge
                          status={(STATUS_TO_KEY[order.status] ?? 'draft') as StatusKey}
                          size="sm"
                        />
                        <ArrowUpRight className="h-4 w-4 text-text-muted shrink-0 rtl-flip" />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Activity feed — 1/3 */}
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
              <CardDescription>Recent changes across your clinic&apos;s orders.</CardDescription>
            </CardHeader>
            <CardBody className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
              {activity.isLoading ? (
                Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
              ) : (activity.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-text-muted">No activity in this clinic yet.</p>
              ) : (
                activity.data!.map((event) => (
                  <div
                    key={event.history_id}
                    className="flex gap-3 pb-4 border-b border-border-subtle last:border-0 last:pb-0"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-1.5 h-2 w-2 rounded-full bg-brand-500 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary">
                        <span className="font-medium">{event.actor_name ?? 'System'}</span>{' '}
                        <span className="text-text-secondary">
                          {describeTransition(event.from_status, event.to_status)}
                        </span>{' '}
                        <span className="font-mono text-text-muted text-xs">
                          {event.order_number}
                        </span>
                      </p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {formatRelative(event.changed_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </CardBody>
          </Card>
        </div>
      </main>
    </AppShell>
  );
}

/**
 * Wraps a KPICard in a button so the user can click it to filter the
 * Active orders list below. Applies a brand-colored ring + shadow when
 * the card matches the currently selected status. Re-clicking the
 * selected card is a no-op (admin pattern, no toggle-off).
 */
function KpiFilterButton({
  filter,
  selected,
  onSelect,
  ariaLabel,
  children,
}: {
  filter: orderQueries.OrderStatusFilter;
  selected: orderQueries.OrderStatusFilter;
  onSelect: (next: orderQueries.OrderStatusFilter) => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  const isActive = selected === filter;
  return (
    <button
      type="button"
      onClick={() => onSelect(filter)}
      aria-pressed={isActive}
      aria-label={ariaLabel}
      className={cn(
        // rounded-lg matches the KPICard's own radius so the active ring
        // hugs the card edge exactly (no visible gap or rounding mismatch).
        'block text-start rounded-lg transition-all',
        isActive
          ? 'ring-2 ring-brand-500 shadow-md'
          : 'hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
      )}
    >
      {children}
    </button>
  );
}

const KPI_FILTER_TITLE: Record<orderQueries.OrderStatusFilter, string> = {
  active: 'Active orders',
  production: 'In production',
  delivery: 'Awaiting pickup',
  delivered: 'Delivered orders',
  submitted: 'Submitted orders',
  pending: 'Pending orders',
  total: 'All orders',
};

function KpiSkeleton() {
  return (
    <Card>
      <CardBody>
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-16 mt-3" />
      </CardBody>
    </Card>
  );
}

function greetingForHour(h: number) {
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function describeTransition(from: string | null, to: string): string {
  if (!from) return `created an order, ${humanStatus(to)}`;
  return `moved to ${humanStatus(to)}`;
}

function humanStatus(s: string): string {
  return s.replace(/_/g, ' ');
}

function formatRelative(s: string): string {
  const then = new Date(s).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} d ago`;
  return new Date(s).toLocaleDateString();
}
